import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { dispatch } from '../store';
import { updateError } from './user';
import { checkPmsEnabled } from './pmsSchedule';
import { seedDateFormatsFromPractice } from '../../utils/dataGridUtils';

// Read cached Dentally enabled state for instant UI on load
const cachedDentallyEnabled = (() => {
    try {
        return localStorage.getItem('dentallyEnabledGlobal') === 'true';
    } catch {
        return false;
    }
})();

const initialState = {
    isLoading: false,
    practice: {},
    clickUpUrlGlobal: '',
    dentallyEnabledGlobal: cachedDentallyEnabled,
};

const slice = createSlice({
    name: 'practice',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, { ...initialState, dentallyEnabledGlobal: false });
            try { localStorage.removeItem('dentallyEnabledGlobal'); } catch { /* noop */ }
        },
        getPracticeData(state, action) {
            state.isLoading = false;
            state.practice = action.payload;
        },
        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        updateLoader(state, action) {
            state.isLoading = action.payload;
        },
        updateClickUpUrlGlobal(state, action) {
            state.clickUpUrlGlobal = action.payload;
        },
        updateDentallyEnabledGlobal(state, action) {
            state.dentallyEnabledGlobal = action.payload;
            try { localStorage.setItem('dentallyEnabledGlobal', String(action.payload)); } catch { /* noop */ }
        }
    }
});

// Reducer
export default slice.reducer;

export const { getPracticeData, reset, updateClickUpUrlGlobal, updateDentallyEnabledGlobal } = slice.actions;

// Standalone fast check — reads from PracticeDentally config (is_active flag).
// Checks the feature flag FIRST to avoid 403 errors for practices without Dentally.
export function checkDentallyEnabled(practiceId) {
    return async () => {
        try {
            // Pass practiceId so the gate refuses stale-practice fires during a
            // practice-switch window (otherwise the bootstrap-safe path lets the
            // call through and the BE returns 403 — DENTALSEM-CRM-1BS3 regression).
            // getPracticeItem dispatches this on every practice load, so it's the
            // single hottest path for the dentally 403 flood.
            if (!hasFeatureAccess('dentally-integration', practiceId)) {
                dispatch(slice.actions.updateDentallyEnabledGlobal(false));
                return;
            }

            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`);
            const results = response?.data?.results || [];
            const isEnabled = results.some((config) => config.is_active);

            dispatch(slice.actions.updateDentallyEnabledGlobal(isEnabled));
        } catch {
            dispatch(slice.actions.updateDentallyEnabledGlobal(false));
        }
    };
}

export function getPracticeItem(id) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            // Fetch practice data and Dentally check in parallel
            const [response] = await Promise.all([
                callApi(`/api/v1/practices/${id}/`),
                dispatch(checkDentallyEnabled(id)),
                dispatch(checkPmsEnabled(id)),
            ]);
            dispatch(slice.actions.getPracticeData(response.data));
            seedDateFormatsFromPractice(response.data?.preferences?.date_format_config);

            if(response.data.metadata && response.data.metadata.length > 0){
                const isClickUpUrl = response.data.metadata.find(item => item.key === 'clickup_url');
                if(isClickUpUrl){
                    dispatch(slice.actions.updateClickUpUrlGlobal(isClickUpUrl.value));
                }
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}


export function updatePractice({ id, data, isEmergencyAppointment = false }) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const response = await callApi(`/api/v1/practices/${id}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(slice.actions.getPracticeData(response.data));
                seedDateFormatsFromPractice(response.data?.preferences?.date_format_config);

                if(response.data.metadata && response.data.metadata.length > 0){
                    const isClickUpUrl = response.data.metadata.find(item => item.key === 'clickup_url');
                    if(isClickUpUrl){
                        dispatch(slice.actions.updateClickUpUrlGlobal(isClickUpUrl.value));
                    }
                }

                if (isEmergencyAppointment) {
                    dispatch(updateError({ success: true, message: 'Data updated successfully.' }));
                    return;
                }
                dispatch(updateError({ success: true, message: 'Data updated successfully. Please refresh the page' }));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.settings?.consent_base_url.toString() || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}

export function generateConsentUrl(id, data, handleCopy) {
    return async () => {
        try {
            dispatch(slice.actions.updateLoader(true));
            const response = await callApi(`/api/v1/practices/${id}/generate_consent_url/`, 'post', data);
            if (response.status === 200) {
                handleCopy(response.data);
                dispatch(updateError({ success: true, message: 'Consent link generated successfully' }));
            } else {
                dispatch(updateError({ success: false, message: response?.data.practice_user?.toString() || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message) || 'Something went wrong', success: false }));
        }
    };
}